import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  DocumentChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  Cog8ToothIcon,
} from '@heroicons/react/24/outline';
import ReactTooltip from 'react-tooltip';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { RECEIVED_SHIPMENTS_REPORTS } from '../../consts/reports';
import { SelectDropdown } from '../../components/SelectDropdown';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { toast } from 'react-toastify';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';
import {
  isValidMonthParam,
  isValidQuarterParam,
  parseMonthParam,
  parseQuarterParam,
  serializeMonth,
  serializeQuarter,
} from '../../hooks/dateParamHelpers';

const SKIP_NULLS = true; // TODO: Delete in the future - for testing only

const DATE_TYPES = {
  ALL_TIME: 'all_time',
  RANGE: 'range',
  MONTH: 'month',
  QUARTER: 'quarter',
};

const quarters = [
  { label: 'Q1', startMonth: 0, endMonth: 2 },
  { label: 'Q2', startMonth: 3, endMonth: 5 },
  { label: 'Q3', startMonth: 6, endMonth: 8 },
  { label: 'Q4', startMonth: 9, endMonth: 11 },
];

function monthLabel(year, monthIndex) {
  const d = new Date(year, monthIndex, 1);
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

function getMonthRange(year, monthIndex) {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getQuarterStartEnd(year, qIndex) {
  const { startMonth, endMonth } = quarters[qIndex];
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, endMonth + 1, 0);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getAllTimeRange() {
  const start = new Date(2020, 0, 1); // 2020-01-01
  const end = new Date();
  end.setHours(23, 59, 59, 999); // End of current day
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getLast30DaysRange() {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 30);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(today) };
}

const ReceivedShipmentsReports = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['received-shipments'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['received-shipments'] || [];
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const [isOpenSettings, setIsOpenSettings] = useState(false);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  const TooltipIcon = ({ tooltipId, label }) => {
    return (
      <span className="inline-flex items-center align-middle">
        <InformationCircleIcon
          className="h-5 w-5 text-blue-600 hover:text-blue-900"
          data-tip={true}
          data-for={tooltipId}
          aria-label={label}
        />
        <ReactTooltip id={tooltipId} effect="solid" place="top">
          <span className="max-w-xs whitespace-normal break-words">{label}</span>
        </ReactTooltip>
      </span>
    );
  };

  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ units_missing: 0, missing_value: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [data]);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;

  // Filters
  const [poSearch, setPoSearch] = useState('');
  const [debouncedPoSearch, setDebouncedPoSearch] = useState('');
  const now = new Date();
  const defaultRange = getLast30DaysRange();
  const defaultMonth = { year: now.getFullYear(), monthIndex: now.getMonth() };
  const defaultQuarter = { quarterYear: now.getFullYear(), quarterIndex: Math.floor(now.getMonth() / 3) };
  const [dateType, setDateType] = useUrlParam(
    'dateType',
    DATE_TYPES.ALL_TIME,
    (value) => Object.values(DATE_TYPES).includes(value),
  );
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultRange.startDate, defaultRange.endDate);

  // Month mode state
  const [monthParam, setMonthParam] = useUrlParam('month', serializeMonth(defaultMonth), isValidMonthParam);
  const monthYear = useMemo(
    () => parseMonthParam(monthParam, defaultMonth),
    [monthParam, defaultMonth.year, defaultMonth.monthIndex],
  );
  const monthInputRef = useRef(null);

  // Quarter mode state
  const [quarterParam, setQuarterParam] = useUrlParam('quarter', serializeQuarter(defaultQuarter), isValidQuarterParam);
  const quarter = useMemo(
    () => parseQuarterParam(quarterParam, defaultQuarter),
    [quarterParam, defaultQuarter.quarterYear, defaultQuarter.quarterIndex],
  );
  const { quarterYear, quarterIndex } = quarter;
  const [isQuarterPickerOpen, setIsQuarterPickerOpen] = useState(false);

  const columns = useMemo(
    () => [
      { key: 'po_number', label: 'PO #', type: 'string', sortable: true, sticky: true },
      { key: 'estimated_delivery', label: 'Estimated Delivery', type: 'date', sortable: true },
      { key: 'order_qty', label: 'Order QTY', type: 'number', sortable: true, align: 'right' },
      { key: 'shipped_qty', label: 'Shiped QTY', type: 'number', sortable: true, align: 'right' },
      { key: 'date_shipped', label: 'Date Shipped', type: 'date', sortable: true },
      { key: 'received_qty', label: 'Recieved Quantity', type: 'number', sortable: true, align: 'right' },
      { key: 'date_received', label: 'Date Recieved', type: 'date', sortable: true },
      { key: 'units_missing', label: 'Units Missing', type: 'number', sortable: true, align: 'right' },
      { key: 'missing_value', label: 'Missing Value', type: 'currency', sortable: true, align: 'right' },
    ],
    []
  );

  // Debounce PO search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPoSearch(poSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [poSearch]);

  const effectiveRange = useMemo(() => {
    if (dateType === DATE_TYPES.ALL_TIME) {
      return getAllTimeRange();
    }
    if (dateType === DATE_TYPES.RANGE) {
      return { startDate, endDate };
    }
    if (dateType === DATE_TYPES.MONTH) {
      return getMonthRange(monthYear.year, monthYear.monthIndex);
    }
    return getQuarterStartEnd(quarterYear, quarterIndex);
  }, [dateType, startDate, endDate, monthYear, quarterYear, quarterIndex]);

  const fetchData = useCallback(async () => {
    if (dateType === DATE_TYPES.RANGE && (!effectiveRange.startDate || !effectiveRange.endDate)) return;
    if (!effectiveRange.startDate || !effectiveRange.endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        page: String(currentPage),
        pageSize: String(pageSize),
      });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      if (debouncedPoSearch) {
        params.append('poNumber', debouncedPoSearch);
      }

      const res = await fetch(`/api/v1/reports/${RECEIVED_SHIPMENTS_REPORTS}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      let filteredData = json.data || [];
      if (SKIP_NULLS) {
        filteredData = filteredData.filter((row) => {
          const unitsMissing = row?.units_missing;
          return unitsMissing !== null && unitsMissing !== undefined && unitsMissing !== 0 && unitsMissing !== "0";
        });
      }
      setData(filteredData);
      setTotals(json.totals || { units_missing: 0, missing_value: 0 });
      setTotalPages(json.totalPages || 1);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotals({ units_missing: 0, missing_value: 0 });
      setTotalPages(1);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [dateType, effectiveRange, currentPage, pageSize, sortField, sortDirection, debouncedPoSearch, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  // Load Received Shipments Email settings on mount
  useEffect(() => {
    dispatch(getReportEmailSettings('received-shipments'));
    dispatch(getReportSendHistory('received-shipments'));
  }, [dispatch]);

  const handleDateChange = ({ startDate: s, endDate: e }) => {
    setDateRange({ startDate: s, endDate: e });
    setCurrentPage(1);
  };

  // Month controls
  const goPrevMonth = () => {
    const m = monthYear.monthIndex - 1;
    if (m < 0) {
      setMonthParam(serializeMonth({ year: monthYear.year - 1, monthIndex: 11 }));
    } else {
      setMonthParam(serializeMonth({ ...monthYear, monthIndex: m }));
    }
    setCurrentPage(1);
  };

  const goNextMonth = () => {
    const m = monthYear.monthIndex + 1;
    if (m > 11) {
      setMonthParam(serializeMonth({ year: monthYear.year + 1, monthIndex: 0 }));
    } else {
      setMonthParam(serializeMonth({ ...monthYear, monthIndex: m }));
    }
    setCurrentPage(1);
  };

  const onPickMonth = (e) => {
    const value = e.target.value; // "YYYY-MM"
    const [y, mm] = value.split('-').map(Number);
    if (y && mm) {
      setMonthParam(serializeMonth({ year: y, monthIndex: mm - 1 }));
      setCurrentPage(1);
    }
  };

  // Quarter controls
  const goPrevQuarter = () => {
    const q = quarterIndex - 1;
    if (q < 0) {
      setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex: 3 }));
    } else {
      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
    }
    setCurrentPage(1);
  };

  const goNextQuarter = () => {
    const q = quarterIndex + 1;
    if (q > 3) {
      setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex: 0 }));
    } else {
      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
    }
    setCurrentPage(1);
  };

  const currentQuarterLabel = `${quarters[quarterIndex].label} ${quarterYear}`;

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSuccessSubmit = async (data) => {
    const payload = {
      emailRecipients: data?.emailRecipients || [],
      scheduleType: data?.schedule || null,
      dayOfMonth: data?.dayOfMonth ?? null,
      slackChannelId: data?.slackChannelId ?? null,
      dayOfWeek: data?.dayOfWeek ?? null,
      timeOfDay: data?.timeOfDay ?? null,
      emailMessage: data?.emailMessage ?? null,
      emailSendPreviewTo: data?.emailSendPreviewTo ?? null
    };
    
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'received-shipments', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (data) => {
    try {
      const payload = {
        email: data?.emailSendPreviewTo,
        emailMessage: data?.emailMessage || '',
        type: data?.type || 'monthly',
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        poNumber: debouncedPoSearch || null
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'received-shipments', ...payload })).unwrap();
      await dispatch(getReportSendHistory('received-shipments')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (data) => {
    try {
      const payload = {
        slackChannelId: data?.slackChannelId,
        emailMessage: data?.emailMessage || '',
        type: data?.type || 'monthly',
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        poNumber: debouncedPoSearch || null
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'received-shipments', ...payload })).unwrap();
      await dispatch(getReportSendHistory('received-shipments')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const formatRangeDate = (value) => {
    if (!value) return '';
    const s = String(value).trim();
    const toPretty = (ymd) => {
      const t = String(ymd).trim();
      // already formatted
      if (t.includes(',') && (t.includes('Jan') || t.includes('Feb') || t.includes('Mar') || t.includes('Apr'))) return t;
      const parts = t.split('-').map((x) => String(x).trim());
      if (parts.length !== 3) return t;
      const [yyyy, mm, dd] = parts;
      const d = new Date(`${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}T00:00:00`);
      if (Number.isNaN(d.getTime())) return t;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };
    const rangeParts = s.split(' - ');
    if (rangeParts.length === 1) return toPretty(rangeParts[0]);
    const left = toPretty(rangeParts[0]);
    const right = toPretty(rangeParts[1]);
    if (left === right) return left;
    return `${left} - ${right}`;
  };

  const renderRows = () => {
    const totalRow = (
      <tr key="__total" className="bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">Total</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right" />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right" />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right" />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" />
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(totals?.units_missing || 0) > 0 ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          {Number(totals?.units_missing || 0).toLocaleString()}
        </td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(totals?.missing_value || 0) > 0 ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          {formatCurrency(totals?.missing_value || 0)}
        </td>
      </tr>
    );

    const rows = (data || []).map((row, idx) => (
      <tr key={`${row.po_number || 'po'}-${idx}`} className="bg-white">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row?.po_id ? (
            <a
              href={`${ROUTES.SHIPPING}?search=${encodeURIComponent(String(row.po_number || ''))}#${row.po_id}`}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
              title="Open in Shipping"
            >
              {row.po_number}
            </a>
          ) : (
            row.po_number
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatRangeDate(row.estimated_delivery)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          <span className="inline-flex items-center justify-end gap-1">
            <span>{Number(row.order_qty || 0).toLocaleString()}</span>
            <TooltipIcon
              tooltipId={`rsr-total-order-${row.po_id || idx}`}
              label={`Total Order QTY: ${Number(row.total_order_qty || 0).toLocaleString()}`}
            />
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          <span className="inline-flex items-center justify-end gap-1">
            <span>{Number(row.shipped_qty || 0).toLocaleString()}</span>
            <TooltipIcon
              tooltipId={`rsr-total-shipped-${row.po_id || idx}`}
              label={`Total Shiped QTY: ${Number(row.total_shipped_qty || 0).toLocaleString()}`}
            />
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatRangeDate(row.date_shipped)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {Number(row.received_qty || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatRangeDate(row.date_received)}</td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(row.units_missing || 0) > 0 ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          {Number(row.units_missing || 0).toLocaleString()}
        </td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(row.missing_value || 0) > 0 ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          {formatCurrency(row.missing_value)}
        </td>
      </tr>
    ));

    return [totalRow, ...rows];
  };

  const filtersComponent = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={poSearch}
          onChange={(e) => {
            setPoSearch(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search PO #"
          className="w-40 sm:w-[160px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      {/* Date type selector */}
      <div className="flex flex-row items-center gap-2">
        <SelectDropdown
          selectClassname="!w-36 !bg-white"
          options={[
            { label: 'All Time', value: DATE_TYPES.ALL_TIME },
            { label: 'Date Range', value: DATE_TYPES.RANGE },
            { label: 'Month', value: DATE_TYPES.MONTH },
            { label: 'Quarter', value: DATE_TYPES.QUARTER },
          ]}
          value={dateType}
          onChange={(value) => {
            setDateType(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Range */}
      {dateType === DATE_TYPES.RANGE && (
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          label="Date Received Range"
          error={error && error.includes('date') ? error : null}
        />
      )}

      {/* Month */}
      {dateType === DATE_TYPES.MONTH && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevMonth}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous month"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[140px]"
            onClick={() => {
              const el = monthInputRef.current;
              if (el && typeof el.showPicker === 'function') {
                try {
                  el.showPicker();
                } catch (e) {
                  el.click();
                }
              } else if (el) {
                el.click();
              }
            }}
            title="Pick month and year"
          >
            {monthLabel(monthYear.year, monthYear.monthIndex)}
          </button>
          <input
            type="month"
            ref={monthInputRef}
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
            onChange={onPickMonth}
            value={`${monthYear.year}-${String(monthYear.monthIndex + 1).padStart(2, '0')}`}
          />
          <button
            type="button"
            onClick={goNextMonth}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next month"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      )}

      {/* Quarter */}
      {dateType === DATE_TYPES.QUARTER && (
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevQuarter}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous quarter"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[110px]"
            onClick={() => setIsQuarterPickerOpen((v) => !v)}
            title="Pick quarter and year"
          >
            {currentQuarterLabel}
          </button>
          <button
            type="button"
            onClick={goNextQuarter}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next quarter"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>

          {isQuarterPickerOpen && (
            <div className="absolute top-full mt-2 z-10 bg-white border border-gray-200 rounded-md shadow w-64 p-3">
              <div className="flex items-center justify-between mb-3">
                <button className="px-2 py-1 border rounded text-sm" onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex }))}>
                  {quarterYear - 1}
                </button>
                <div className="font-medium">{quarterYear}</div>
                <button className="px-2 py-1 border rounded text-sm" onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex }))}>
                  {quarterYear + 1}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quarters.map((q, idx) => (
                  <button
                    key={q.label}
                    className={`px-2 py-2 border rounded text-sm ${
                      idx === quarterIndex ? 'bg-blue-50 border-blue-300' : 'bg-white'
                    }`}
                    onClick={() => {
                      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: idx }));
                      setIsQuarterPickerOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {q.label} {quarterYear}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Cog8ToothIcon
            className="h-5 w-5 text-primary cursor-pointer"
            onClick={() => {
                setIsOpenSettings(true);
            }}
        />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={[
        { key: 'po_number', label: 'PO #' },
        { key: 'estimated_delivery', label: 'Estimated Delivery' },
        { key: 'order_qty', label: 'Order QTY' },
        { key: 'shipped_qty', label: 'Shiped QTY' },
        { key: 'date_shipped', label: 'Date Shipped' },
        { key: 'received_qty', label: 'Recieved Quantity' },
        { key: 'date_received', label: 'Date Recieved' },
        { key: 'units_missing', label: 'Units Missing' },
        { key: 'missing_value', label: 'Missing Value' },
      ]}
      filename={`${RECEIVED_SHIPMENTS_REPORTS}`}
      onExport={async () => {
        const params = new URLSearchParams({
          startDate: effectiveRange.startDate,
          endDate: effectiveRange.endDate,
        });
        if (sortField) {
          params.append('sortField', sortField);
          params.append('sortDirection', sortDirection);
        }
        if (debouncedPoSearch) {
          params.append('poNumber', debouncedPoSearch);
        }
        const res = await fetch(`/api/v1/reports/${RECEIVED_SHIPMENTS_REPORTS}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${RECEIVED_SHIPMENTS_REPORTS}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <>
      <ReportTemplate
        reportId={RECEIVED_SHIPMENTS_REPORTS}
        description="PO receiving status (estimated delivery, shipped vs received, missing units/value). Filtered by day 1 of receiving."
        breadcrumbs={breadcrumbs}
        onBack={handleBackToReports}
        marginFromSidebar={marginFromSidebar}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={data}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No received shipments for the selected period."
        onSort={handleSort}
        sortField={sortField}
        cronType={emailSettings?.cronType || null}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {renderRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'received-shipments'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="received-shipments"
      />
    </>
  );
};

export default ReceivedShipmentsReports;
